/**
 * CC-53921 (spec 157) — what the General Ledger page says about CB's sub-account rows.
 *
 * CB's GL export writes every SUB-account row one cell short (the amount under Split,
 * the running balance under Amount). cb-api puts those rows back under CB's headings
 * wherever CB's own structure and figures prove where each cell belongs, and reports
 * the outcome as `data.subAccountColumns` (cb-api glSubAccountColumns.js):
 *   { realignedRows, realignedSections, alignedSections, refused: [{ account, reason }] }
 * This module turns that into the page's words, and into which files may leave the
 * page; it never moves or computes a figure.
 *
 * Three states must never look alike:
 *   - checked, and every sub-account row is under its heading → say what moved;
 *   - checked, and some sections could not be verified → WARN, naming each;
 *   - not checked at all (a result saved before cb-api checked, or a fresh run
 *     answered without the report) → say so.
 * And every file that leaves the page either says the same thing or is refused.
 */

/** cb-api's report, or `undefined` (never checked) / `null` (nothing to check). */
export function readSubAccountColumns(data) {
  if (!data || !('subAccountColumns' in data)) return undefined
  const s = data.subAccountColumns
  if (s == null || typeof s !== 'object') return null
  const refused = Array.isArray(s.refused)
    ? s.refused.filter((r) => r && typeof r.account === 'string' && r.account !== '')
    : []
  return {
    realignedRows: Number.isFinite(s.realignedRows) ? s.realignedRows : 0,
    realignedSections: Number.isFinite(s.realignedSections) ? s.realignedSections : 0,
    refused,
  }
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

/** The informational line when rows were moved (role="status"). */
export function realignedNotice(summary) {
  if (!summary || summary.realignedRows <= 0) return null
  return `${plural(summary.realignedRows, 'sub-account row', 'sub-account rows')} in ` +
    `${plural(summary.realignedSections, 'account', 'accounts')} ` +
    `${summary.realignedRows === 1 ? 'was' : 'were'} moved under ${summary.realignedRows === 1 ? 'its' : 'their'} column headings, ` +
    "because Centerbase's export writes sub-account rows one column short. " +
    'Ref Number is blank on those rows: Centerbase does not include it.'
}

// "Columns not verified for 1 account: Retainer Income." A refused section is
// called an ACCOUNT, never a sub-account: cb-api also flags a top-level ("Totals")
// section that holds a short-looking row, and a section whose headings changed
// (review of PR 332).
const unverifiedLead = (refused) =>
  `Columns not verified for ${plural(refused.length, 'account', 'accounts')}: ${refused.map((r) => r.account).join('; ')}.`

/** The warning when a section could not be verified (role="alert"). */
export function refusedWarning(summary) {
  if (!summary || summary.refused.length === 0) return null
  const n = summary.refused.length
  return `${unverifiedLead(summary.refused)} ` +
    `${n === 1 ? 'It is' : 'They are'} shown exactly as Centerbase wrote ${n === 1 ? 'it' : 'them'}, ` +
    'so a figure under Amount may be the running balance and the amount may be under Split.'
}

/**
 * Rows present and no report at all. Two different situations, so two sets of words
 * (review of PR 332): a result reopened from the tray was produced before the check
 * existed, and a new run checks it; a FRESH run without the report came from a
 * worker without the check (a deploy in progress) or from a job reused from before
 * it, and running again is not promised to change that.
 */
export function uncheckedNotice({ saved }) {
  return saved
    ? 'This saved result was produced before Report Center checked sub-account columns, so sub-account rows may be one column short. Run the report again to check them.'
    : "Report Center did not check this result's sub-account columns, so sub-account rows may be one column short: the amount under Split and the running balance under Amount."
}

/** The per-section marker text, beside the account heading. */
export const UNVERIFIED_SECTION_LABEL = 'Columns not verified'

/** The headings (trimmed) of the sections cb-api could not verify. */
export const refusedAccounts = (summary) => new Set((summary?.refused || []).map((r) => r.account.trim()))

const UNCHECKED_LEAD = "Report Center did not check this result's sub-account columns"

/**
 * Why CB's workbook for this run is refused, or null when the page has nothing to
 * say and the server decides (cb-api refuses with 409 if it must).
 *
 * CB's workbook comes from the same writer as its CSV and cb-api cannot move cells
 * inside it, so a run that moved rows, or could not verify a section, or was never
 * checked, does not download it. The advice names only the CSV Report Center builds
 * on the server — which keeps CB's account headings — and says exactly what that
 * file does in each case. It never recommends the client-built Excel ("Only visible
 * columns, current order"): that file is built from leaf rows and carries no
 * account headings at all (review of PR 332).
 *
 * @param {object|null|undefined} summary readSubAccountColumns(data)
 * @param {{ hasRows: boolean }} opts — rows on screen; with none, only the server knows
 */
export function glXlsxRefusal(summary, { hasRows }) {
  if (summary === undefined) {
    return hasRows
      ? `${UNCHECKED_LEAD}, so Centerbase's Excel file, which may put sub-account amounts under the wrong columns, was not downloaded. Run the report again, then export.`
      : null
  }
  if (!summary) return null
  const n = summary.refused.length
  if (n > 0) {
    return "Centerbase's Excel file for this General Ledger may put sub-account amounts under the wrong columns, so it was not downloaded. " +
      `${unverifiedLead(summary.refused)} ` +
      `Download CSV instead: it shows ${n === 1 ? 'that account' : 'those accounts'} exactly as Centerbase wrote ${n === 1 ? 'it' : 'them'}, ` +
      `under a WARNING line that names ${n === 1 ? 'it' : 'them'}` +
      (summary.realignedRows > 0 ? ', and moves the other sub-account rows under their column headings.' : '.')
  }
  if (summary.realignedRows > 0) {
    return "Centerbase's Excel file for this General Ledger puts sub-account amounts under the wrong columns, so it was not downloaded. " +
      'Download CSV instead: Report Center moves those rows under their column headings.'
  }
  return null
}

/**
 * The server refused the workbook (409) for a run the page holds no report for —
 * the rows are not on screen yet, or the filters changed since. The page cannot know
 * which case cb-api met, so these words promise only what the CSV does in every one.
 */
export const GL_XLSX_SERVER_REFUSAL_MESSAGE =
  "Centerbase's Excel file for this General Ledger would carry sub-account rows that may be under the wrong columns, so it was not downloaded. " +
  'Download CSV instead: Report Center moves the sub-account rows it can verify under their column headings, and names any account it cannot in a WARNING line.'

/**
 * Why a file built from the grid's view (ReportExport's client-built CSV / Excel /
 * PDF) is refused for this run, or null to build it as usual. Published on the view
 * as `clientExportRefusal(format)` (see clientExportRefusal.js).
 *
 * A client-built CSV or Excel carries LEAF rows only — no account headings — so the
 * "Columns not verified" marker on a refused section's heading never reaches it, and
 * that section's running balances would land under Amount as summable numbers with
 * nothing in the file saying so (review of PR 332, reproduced on the live fixture).
 * The PDF prints the headings, marker included, so it is built. A run that was never
 * checked has no marker anywhere, so no client-built file is.
 *
 * `currencyChoice`: the grid holds an explicit "Show $ on amounts" choice, which makes
 * ReportExport build EVERY CSV and Excel client-side (CC-50004) — so "untick every
 * export option" alone would be refused again; the advice says how to clear it.
 */
export function glClientExportRefusal(summary, { hasRows, currencyChoice = false }, format) {
  if (summary === undefined) {
    return hasRows
      ? `${UNCHECKED_LEAD}, so a file built from it could carry sub-account amounts under the wrong columns with nothing saying so. It was not built. Run the report again, then export.`
      : null
  }
  if (!summary || summary.refused.length === 0 || format === 'pdf') return null
  const n = summary.refused.length
  const toServerCsv = currencyChoice
    ? 'Download CSV with no export option ticked, after "Reset grid layout" clears your "Show $ on amounts" choice'
    : 'Download CSV with no export option ticked'
  return `${unverifiedLead(summary.refused)} ` +
    `A file built from this view would carry ${n === 1 ? 'its' : 'their'} rows exactly as Centerbase wrote them, with nothing saying so, so it was not built. ` +
    `${toServerCsv}: Report Center's own CSV names ${n === 1 ? 'that account' : 'those accounts'} in a WARNING line. ` +
    `The PDF marks ${n === 1 ? 'it' : 'them'} on ${n === 1 ? 'its heading' : 'their headings'}.`
}
